import React, { ReactNode, FC, useCallback, useMemo, useState, useEffect, ComponentType } from 'react';

import PageErrorBoundary, { PageErrorBoundaryProps } from 'ia-react-core/~/components/error/PageErrorBoundary';
import { useDispatch } from 'react-redux';
import setRouterPageLoading from 'ia-react-core/~/actions/router-fn/setRouterPageLoading';
import setRouterPageLoaded from 'ia-react-core/~/actions/router-fn/setRouterPageLoaded';
import setRouterPageLoadingError from 'ia-react-core/~/actions/router-fn/setRouterPageLoadingError';
import Loader from '../Loader';
import useTranslation from '../../../hooks/useTranslation';

export interface PageLoaderProps extends Omit<PageErrorBoundaryProps, 'children'> {
  children?: ReactNode | (() => ReactNode);
  /** Function returning a dynamic import loading the page. */
  loader: () => Promise<{ default: ComponentType<any> }>;
  /** Custom error message. */
  errorMessage?: string;
  /** Custom retry button text. */
  errorRetryButton?: string;
  /** Any props from PageErrorBoundary Replace the custom loader */
  customLoader?: ReactNode | (() => ReactNode);
  /** Promise to delay the pageView trigger */
  delayPageViewUntil?: () => Promise<void>;
}

export interface PageLoaderInternalProps {
  pageLoaderId: number;
}

export interface PageLoaderComposedProps extends PageLoaderProps, PageLoaderInternalProps {}

/**
 * @mode developer
 */
const PageLoader: FC<PageLoaderComposedProps> = ({
  pageLoaderId, children, loader, errorTitle, errorMessage, errorRetryButton, customLoader, delayPageViewUntil, ...props
}) => {
  const { t } = useTranslation('PageLoader');
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const [CustomLoadable, setCustomLoadable] = useState<ComponentType<any>>();
  const dispath = useDispatch();

  const load = useCallback(async () => {
    setHasLoadingError(false);
    dispath(setRouterPageLoading(pageLoaderId));
    const { default: Component } = await loader();
    setCustomLoadable(() => Component);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Promise.resolve(delayPageViewUntil?.()).finally(() => {
      dispath(setRouterPageLoaded(pageLoaderId));
    });
  // We mustn't watch loader & delayPageViewUntil
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispath, pageLoaderId]);

  useEffect(() => {
    load().catch(() => {
      dispath(setRouterPageLoadingError(pageLoaderId));
      setHasLoadingError(true);
    });
  }, [dispath, load, pageLoaderId]);

  const handleClick = useCallback(() => window.location.reload(), []);

  const indicator = useMemo(() => {
    if (customLoader) return typeof customLoader === 'function' ? customLoader() : customLoader;
    return <Loader />;
  }, [customLoader]);

  return (
    <PageErrorBoundary
      errorTitle={(typeof errorTitle !== 'undefined') ? errorTitle : t('error.title')}
      message={errorMessage}
      retryButton={(typeof errorRetryButton !== 'undefined') ? errorRetryButton : t('error.retryButton')}
      onRetry={handleClick}
      forceError={hasLoadingError}
      {...props}
    >
      {CustomLoadable ? (
        <CustomLoadable {...props}>
          {typeof children === 'function' ? children() : children}
        </CustomLoadable>
      ) : <div>{indicator}</div>}
    </PageErrorBoundary>
  );
};

export default PageLoader;
